import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Alert, Button, Col, Dropdown, Empty, Layout, Row, Spin, Tooltip, message } from "antd";
import { FileExcelOutlined, PlusOutlined } from "@ant-design/icons";

import {
  accountActions,
  businessActions,
  periodsActions,
  reportingActions,
  scenarioActions,
} from "../../redux/actions";

import OverlayedLoader from "../../components/OverlayedLoader";
import RenderUpdater from "../../components/RenderUpdater";
import { computeAllocationsHash, computePeriodsHash } from "../../utils/assemblyKey";
import { buildCalculationAccountStructure } from "../../utils/accountStructure/accountStructureUtils";

import ReportWidget from "./reportWidget";
import WidgetSettingsModal from "./widgetSettingsModal";
import BulkAddSeriesModal from "./bulkAddSeriesModal";
import { WIDGET_TYPE_OPTIONS, getWidgetType } from "./widgetRegistry";
import { downloadWorkbook, exportFileName } from "../../utils/tableExport";
import {
  DEFAULT_WIDGET_SETTINGS,
  EMPTY_SERIES_FIELDS,
  ROW_KINDS,
  accountKey,
  applyChartCompare,
  buildGlobalFormat,
  buildWidgetTable,
  compactSeriesStyle,
  getAccountsForTab,
  getChartCompare,
  getFromScenarioMap,
  getPeriodCategories,
  getTabNames,
  getWidgetPeriods,
  isCustomTable,
  normalizeWidget,
  resolveSeries,
  scenarioHasValues,
  tableExportRows,
  toId,
  toWidgetPayload,
  widgetSourceKeys,
} from "./reportingUtils";

const { Content } = Layout;
const NO_ACCOUNTS = [];
const ADD_MENU_ITEMS = WIDGET_TYPE_OPTIONS.map(({ value, label }) => ({ key: value, label }));
const nextSortOrder = (widget) => widget.series.reduce((m, r) => Math.max(m, r.sort_order ?? 0), -1) + 1;

const Reporting = () => {
  const dispatch = useDispatch();

  // ---------------------------------------------------------------------------
  // Redux
  // ---------------------------------------------------------------------------
  const {
    accountStructure,
    periods,
    businessCases,
    assemblingCases,
    assembledBusinessCases,
    assemblingCasesError,
    scenariosList,
    scenarioMap,
    compilingScenario,
    widgetsRaw,
    organization,
    updatingOrganization,
    allocations,
    fetchingAllocations,
  } = useSelector(
    (state) => ({
      accountStructure: state.accounts.accountStructure,
      periods: state.periods.periods,

      businessCases: state.business.businessCases,
      assemblingCases: state.business.assemblingCases,
      assembledBusinessCases: state.business.assembledBusinessCases,
      assemblingCasesError: state.business.assemblingCasesError,

      scenariosList: state.scenarios.scenariosList,
      scenarioMap: state.scenarios.scenarios,
      compilingScenario: state.scenarios.compilingScenario,

      // Array of reporting_widget rows, each with a nested `series` array.
      widgetsRaw: state.reporting.widgets,

      organization: state.organization.organization,
      updatingOrganization: state.organization.updatingOrganization,

      allocations: state.allocations.allocations,
      fetchingAllocations: state.allocations.fetchingAllocations,
    }),
    shallowEqual
  );

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [assemblingTotals, setAssemblingTotals] = useState(false);
  const [initialBoot, setInitialBoot] = useState(true);
  const [settingsWidgetId, setSettingsWidgetId] = useState(null);
  const [bulkAddWidgetId, setBulkAddWidgetId] = useState(null);
  const [compileAttempted, setCompileAttempted] = useState(() => new Set());

  const assemblingRef = useRef(false);
  const fetchingScenarioRef = useRef(new Set());
  const resetTabRef = useRef(new Set());
  const prevInputsKeyRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Initial loads
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatch(accountActions.getAccountStructure());
    dispatch(businessActions.getBusinessCases());
    dispatch(reportingActions.getReportingWidgets());
    dispatch(periodsActions.getPeriods());
    dispatch(scenarioActions.getScenariosList());
  }, [dispatch]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const widgets = useMemo(
    () =>
      widgetsRaw == null
        ? null
        : widgetsRaw
            .map(normalizeWidget)
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.widget_id - b.widget_id),
    [widgetsRaw]
  );

  const allSeries = useMemo(() => (widgets || []).flatMap((w) => w.series), [widgets]);

  // Series sources plus the ones custom table columns use ("bc:<id>" / "sc:<id>").
  const neededSourceKeys = useMemo(() => [...new Set((widgets || []).flatMap(widgetSourceKeys))], [widgets]);

  const neededBcIds = useMemo(
    () =>
      neededSourceKeys
        .filter((k) => k.startsWith("bc:"))
        .map((k) => toId(k.slice(3)))
        .filter((id) => id !== -1),
    [neededSourceKeys]
  );

  const neededScenarioIds = useMemo(
    () =>
      neededSourceKeys
        .filter((k) => k.startsWith("sc:"))
        .map((k) => toId(k.slice(3)))
        .filter((id) => id !== -1)
        .map(String),
    [neededSourceKeys]
  );

  const calculationAccountStructure = useMemo(
    () => (accountStructure ? buildCalculationAccountStructure(accountStructure) : null),
    [accountStructure]
  );

  const periodCats = useMemo(() => getPeriodCategories(periods), [periods]);
  const globalFmt = useMemo(() => buildGlobalFormat(organization), [organization]);

  const assembledById = useMemo(
    () => new Map((assembledBusinessCases || []).map((bc) => [toId(bc.business_case_id), bc])),
    [assembledBusinessCases]
  );

  const knownBcIds = useMemo(
    () => new Set((businessCases || []).map((bc) => toId(bc.business_case_id))),
    [businessCases]
  );

  // Needed, still exists, and not assembled yet. Failed ones carry `error`
  // and count as "done" so they don't retry forever.
  const missingBcIds = useMemo(
    () => neededBcIds.filter((id) => knownBcIds.has(id) && !assembledById.has(id)),
    [neededBcIds, knownBcIds, assembledById]
  );
  const missingBcSet = useMemo(() => new Set(missingBcIds), [missingBcIds]);

  const failedBcTotals = useMemo(
    () =>
      neededBcIds
        .map((id) => assembledById.get(id))
        .filter((bc) => bc?.error)
        .map((bc) => ({
          id: toId(bc.business_case_id),
          name: bc.name ?? `Business case ${bc.business_case_id}`,
          error: bc.error,
        })),
    [neededBcIds, assembledById]
  );

  const tabNames = useMemo(() => getTabNames(accountStructure), [accountStructure]);

  const accountsByTab = useMemo(
    () => new Map(tabNames.map((t) => [t, getAccountsForTab(accountStructure, t)])),
    [accountStructure, tabNames]
  );

  const accountNameByKey = useMemo(() => {
    const m = new Map();
    accountsByTab.forEach((list) =>
      list.forEach((a) => {
        const k = accountKey(a);
        if (!m.has(k)) m.set(k, a.name);
      })
    );
    return m;
  }, [accountsByTab]);

  // A scenario is pending until it's fetched and either has values or a
  // compile attempt has finished (so a scenario with no values can't block forever).
  const pendingScenarioSet = useMemo(
    () =>
      new Set(
        neededScenarioIds.filter((sid) => {
          const sc = getFromScenarioMap(scenarioMap, sid);
          if (!sc) return true;
          if (scenarioHasValues(sc)) return false;
          return compilingScenario || !compileAttempted.has(sid);
        })
      ),
    [neededScenarioIds, scenarioMap, compilingScenario, compileAttempted]
  );

  // ---------------------------------------------------------------------------
  // Invalidate totals when calculation inputs change
  // ---------------------------------------------------------------------------
  const inputsKey = useMemo(() => {
    const allocHash = computeAllocationsHash(Array.isArray(allocations) ? allocations : []);
    const periodsHash = computePeriodsHash(periods || []);
    return `exp:${organization?.expanded_periods ?? ""}|p:${periodsHash}|a:${allocHash}`;
  }, [allocations, periods, organization?.expanded_periods]);

  useEffect(() => {
    const prev = prevInputsKeyRef.current;
    prevInputsKeyRef.current = inputsKey;
    if (prev === null || prev === inputsKey) return;

    dispatch(businessActions.clearAssembledBusinessCases());
    setCompileAttempted(new Set());
  }, [dispatch, inputsKey]);

  // ---------------------------------------------------------------------------
  // Assemble business case totals that are needed but missing
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!businessCases || missingBcIds.length === 0) return;
    if (assemblingRef.current || assemblingCases || assemblingCasesError) return;

    // Pass every needed BC (as the old page did); the batch action skips
    // the ones already assembled unless forced.
    const needed = new Set(neededBcIds);
    const toAssemble = businessCases.filter((bc) => needed.has(toId(bc.business_case_id)));
    if (toAssemble.length === 0) return;

    assemblingRef.current = true;
    setAssemblingTotals(true);

    Promise.resolve(dispatch(businessActions.assembleBusinessCaseTotalsBatch(toAssemble))).finally(() => {
      assemblingRef.current = false;
      setAssemblingTotals(false);
    });
  }, [dispatch, businessCases, missingBcIds, neededBcIds, assemblingCases, assemblingCasesError]);

  // ---------------------------------------------------------------------------
  // Fetch + compile scenarios referenced by any series
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!calculationAccountStructure || fetchingAllocations) return;
    if (neededScenarioIds.length === 0) return;

    // 1) Fetch the first scenario we don't have yet
    for (const sid of neededScenarioIds) {
      if (!getFromScenarioMap(scenarioMap, sid) && !fetchingScenarioRef.current.has(sid)) {
        fetchingScenarioRef.current.add(sid);
        Promise.resolve(dispatch(scenarioActions.getScenario(Number(sid)))).finally(() =>
          fetchingScenarioRef.current.delete(sid)
        );
        return;
      }
    }

    // 2) Compile one scenario at a time, each at most once per set of inputs
    if (compilingScenario) return;

    const toCompile = neededScenarioIds.find((sid) => {
      const sc = getFromScenarioMap(scenarioMap, sid);
      return sc && !scenarioHasValues(sc) && !compileAttempted.has(sid);
    });
    if (!toCompile) return;

    setCompileAttempted((prev) => new Set(prev).add(toCompile));
    dispatch(
      scenarioActions.calculateConvergence(
        getFromScenarioMap(scenarioMap, toCompile),
        calculationAccountStructure,
        allocations
      )
    );
  }, [
    dispatch,
    neededScenarioIds,
    scenarioMap,
    calculationAccountStructure,
    allocations,
    fetchingAllocations,
    compilingScenario,
    compileAttempted,
  ]);

  // ---------------------------------------------------------------------------
  // Clear tab/account on series whose custom tab was deleted
  // (was done during render before; now an effect)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!accountStructure || !widgets) return;
    const valid = new Set(tabNames);

    allSeries
      .filter((r) => r.tab_name && !valid.has(r.tab_name) && !resetTabRef.current.has(r.series_id))
      .forEach((r) => {
        resetTabRef.current.add(r.series_id);
        dispatch(
          reportingActions.updateReportingSeries({
            ...r,
            tab_name: "",
            account_id: -1,
            associated_account_id: -1,
            account_number: "",
          })
        );
      });
  }, [dispatch, accountStructure, widgets, allSeries, tabNames]);

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  const isLoadingData =
    widgets == null ||
    assemblingCases ||
    assemblingTotals ||
    fetchingAllocations ||
    compilingScenario ||
    missingBcIds.length > 0 ||
    pendingScenarioSet.size > 0;

  useEffect(() => {
    if (initialBoot && !isLoadingData) setInitialBoot(false);
  }, [initialBoot, isLoadingData]);

  // ---------------------------------------------------------------------------
  // Shared context for widgets
  // ---------------------------------------------------------------------------
  const sourceOptions = useMemo(
    () => [
      {
        label: "Business cases",
        options: (businessCases || []).map((bc) => ({
          value: `bc:${bc.business_case_id}`,
          label: bc.name,
        })),
      },
      {
        label: "Scenarios",
        options: (scenariosList || []).map((s) => ({
          value: `sc:${s.scenario_id}`,
          label: s.name,
        })),
      },
    ],
    [businessCases, scenariosList]
  );

  const tabOptions = useMemo(() => tabNames.map((t) => ({ value: t, label: t })), [tabNames]);

  const ctx = useMemo(
    () => ({
      periodCats,
      globalFmt,
      assembledById,
      scenarioMap,
      scenariosList,
      sourceOptions,
      tabOptions,
      getAccountsForTab: (tab) => accountsByTab.get(tab) || NO_ACCOUNTS,
      accountName: (row) => accountNameByKey.get(accountKey(row)) || "",
      isSeriesPending: (row) => {
        const bcId = toId(row.business_case_id);
        if (bcId !== -1) return missingBcSet.has(bcId);
        const scId = toId(row.scenario_id);
        return scId !== -1 && pendingScenarioSet.has(String(scId));
      },
    }),
    [
      periodCats,
      globalFmt,
      assembledById,
      scenarioMap,
      scenariosList,
      sourceOptions,
      tabOptions,
      accountsByTab,
      accountNameByKey,
      missingBcSet,
      pendingScenarioSet,
    ]
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const updateSeries = useCallback(
    (row, patch) => dispatch(reportingActions.updateReportingSeries({ ...row, ...patch })),
    [dispatch]
  );

  const handlers = useMemo(
    () => ({
      // value is "bc:<id>", "sc:<id>" or undefined (cleared)
      setSource: (row, value) => {
        if (!value) return updateSeries(row, EMPTY_SERIES_FIELDS);
        const [kind, id] = value.split(":");
        return updateSeries(
          row,
          kind === "bc"
            ? { business_case_id: Number(id), scenario_id: -1 }
            : { scenario_id: Number(id), business_case_id: -1 }
        );
      },

      setTab: (row, tabName) =>
        updateSeries(row, {
          tab_name: tabName || "",
          account_id: -1,
          associated_account_id: -1,
          account_number: "",
        }),

      setAccount: (row, account) =>
        updateSeries(
          row,
          account
            ? {
                account_id: toId(account.account_id),
                associated_account_id: toId(account.associated_account_id),
                account_number: account.account_number ?? "",
              }
            : { account_id: -1, associated_account_id: -1, account_number: "" }
        ),

      addSeries: (widget) =>
        dispatch(
          reportingActions.createReportingSeries({
            ...EMPTY_SERIES_FIELDS,
            widget_id: widget.widget_id,
            sort_order: nextSortOrder(widget),
          })
        ),

      // kind: ROW_KINDS.blank or ROW_KINDS.heading (headings start out bold)
      addSpacerRow: (widget, kind) =>
        dispatch(
          reportingActions.createReportingSeries({
            ...EMPTY_SERIES_FIELDS,
            widget_id: widget.widget_id,
            sort_order: nextSortOrder(widget),
            style: compactSeriesStyle({ kind, bold: kind === ROW_KINDS.heading }),
          })
        ),

      setRowStyle: (row, patch) => updateSeries(row, { style: compactSeriesStyle({ ...row.style, ...patch }) }),

      openBulkAdd: (widget) => setBulkAddWidgetId(widget.widget_id),

      toggleSelection: (widget) =>
        dispatch(
          reportingActions.updateReportingWidget(
            toWidgetPayload(widget, {
              settings: { ...widget.settings, hideSelection: !widget.settings.hideSelection },
            })
          )
        ),

      removeSeries: (row) => dispatch(reportingActions.deleteReportingSeries(row.series_id)),

      // Series are already in display order; swap with the neighbour and save
      // the whole order (also repairs duplicate sort_orders).
      moveSeries: (row, direction) => {
        const widget = (widgets || []).find((w) => w.widget_id === row.widget_id);
        if (!widget) return;
        const order = widget.series.map((s) => s.series_id);
        const from = order.indexOf(row.series_id);
        const to = from + direction;
        if (from < 0 || to < 0 || to >= order.length) return;

        [order[from], order[to]] = [order[to], order[from]];
        dispatch(reportingActions.reorderReportingSeries(widget.widget_id, order));
      },

      openSettings: (widget) => setSettingsWidgetId(widget.widget_id),

      deleteWidget: (widget) => dispatch(reportingActions.deleteReportingWidget(widget.widget_id)),

      // Swap with the neighbour, then write back any positions that changed
      // (also repairs duplicate positions).
      moveWidget: (widget, direction) => {
        const order = [...(widgets || [])];
        const from = order.findIndex((w) => w.widget_id === widget.widget_id);
        const to = from + direction;
        if (from < 0 || to < 0 || to >= order.length) return;

        [order[from], order[to]] = [order[to], order[from]];
        order.forEach((w, index) => {
          if (w.position !== index) {
            dispatch(reportingActions.updateReportingWidget(toWidgetPayload(w, { position: index })));
          }
        });
      },
    }),
    [dispatch, updateSeries, widgets]
  );

  const addWidget = useCallback(
    (type) => {
      const position = (widgets || []).reduce((m, w) => Math.max(m, w.position ?? 0), -1) + 1;
      dispatch(
        reportingActions.createReportingWidget({
          type,
          position,
          settings: JSON.stringify(DEFAULT_WIDGET_SETTINGS),
        })
      );
    },
    [dispatch, widgets]
  );

  const saveSettings = useCallback(
    (widget, { type, settings }) => {
      setSettingsWidgetId(null);
      dispatch(reportingActions.updateReportingWidget(toWidgetPayload(widget, { type, settings })));
    },
    [dispatch]
  );

  // One workbook, one sheet per chart/table with data, in page order.
  const exportAll = useCallback(() => {
    const sheets = (widgets || [])
      .map((w) => {
        const def = getWidgetType(w.type);
        const cats = getWidgetPeriods(ctx.periodCats, w);
        const series = applyChartCompare(
          w.series.map((row) => resolveSeries(row, ctx, cats)).filter(Boolean),
          getChartCompare(w, def)
        );
        const name = w.settings.title || def.label;
        const table = buildWidgetTable(w, ctx, cats, series);
        const hasData = def.kind === "table" ? table.rows.length > 0 : series.length > 0;
        return hasData ? { name, rows: tableExportRows(table) } : null;
      })
      .filter(Boolean);

    if (sheets.length === 0) {
      message.info("No charts or tables have data to export yet.");
      return;
    }
    downloadWorkbook(exportFileName("Reporting"), sheets);
  }, [widgets, ctx]);

  // Keeps the dialog open if the request fails, so the selection isn't lost.
  const addSeriesBulk = useCallback(
    async (widget, rows) => {
      const created = await dispatch(reportingActions.createReportingSeriesBulk(widget.widget_id, rows));
      if (created) setBulkAddWidgetId(null);
      else message.error("Unable to add the accounts. Please try again.");
    },
    [dispatch]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (accountStructure == null || businessCases == null || periods == null || widgets == null) {
    return <OverlayedLoader />;
  }
  if (updatingOrganization) return <RenderUpdater />;

  const addMenu = { items: ADD_MENU_ITEMS, onClick: ({ key }) => addWidget(key) };
  const settingsWidget = widgets.find((w) => w.widget_id === settingsWidgetId) ?? null;
  const bulkAddWidget = widgets.find((w) => w.widget_id === bulkAddWidgetId) ?? null;

  return (
    <>
      <Layout style={{ height: "100vh", marginLeft: -16, position: "relative" }}>
        <Content style={{ width: "100%", overflow: "auto", padding: "19px 16px 32px" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
            {widgets.length > 0 && (
              <Tooltip
                title={isLoadingData ? "Still calculating…" : "One sheet per chart or table, as shown"}
              >
                <Button icon={<FileExcelOutlined />} disabled={isLoadingData} onClick={exportAll}>
                  Export all to Excel
                </Button>
              </Tooltip>
            )}
            <Dropdown menu={addMenu} trigger={["click"]}>
              <Button type="primary" icon={<PlusOutlined />}>
                Add chart or table
              </Button>
            </Dropdown>
          </div>

          {failedBcTotals.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Some business case totals could not be calculated"
              description={
                <div>
                  {failedBcTotals.slice(0, 8).map((x) => (
                    <div key={x.id}>
                      {x.name} (id {x.id}): {x.error}
                    </div>
                  ))}
                  {failedBcTotals.length > 8 && <div>…and {failedBcTotals.length - 8} more.</div>}
                </div>
              }
            />
          )}

          {widgets.length === 0 ? (
            <Empty
              style={{ marginTop: 80 }}
              description="Add a chart or table to start building your report."
            >
              <Dropdown menu={addMenu} trigger={["click"]}>
                <Button type="primary" icon={<PlusOutlined />}>
                  Add chart or table
                </Button>
              </Dropdown>
            </Empty>
          ) : (
            <Row gutter={[16, 16]}>
              {widgets.map((w, i) => (
                <Col key={w.widget_id} xs={24} xl={w.settings.span === 24 ? 24 : 12}>
                  <ReportWidget
                    widget={w}
                    isFirst={i === 0}
                    isLast={i === widgets.length - 1}
                    ctx={ctx}
                    handlers={handlers}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Content>
      </Layout>

      {settingsWidget && (
        <WidgetSettingsModal
          key={settingsWidget.widget_id}
          widget={settingsWidget}
          globalFmt={globalFmt}
          periodCats={periodCats}
          sourceOptions={sourceOptions}
          onCancel={() => setSettingsWidgetId(null)}
          onSave={saveSettings}
        />
      )}

      {bulkAddWidget && (
        <BulkAddSeriesModal
          key={bulkAddWidget.widget_id}
          widget={bulkAddWidget}
          ctx={ctx}
          hideSource={isCustomTable(bulkAddWidget)}
          onCancel={() => setBulkAddWidgetId(null)}
          onSave={addSeriesBulk}
        />
      )}

      {/* Full-page overlay only while the page first loads */}
      {initialBoot && isLoadingData && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 12,
            color: "#fff",
          }}
        >
          <Spin size="large" />
          <div style={{ fontWeight: 600, fontSize: 16 }}>Retrieving data…</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>Calculating business cases and scenarios</div>
        </div>
      )}
    </>
  );
};

export default Reporting;